import React from 'react'
import { AlertTriangle, CheckCircle, Shield } from 'lucide-react'
import type { VerificationFlags } from '../../../../types/incomeBreakdown.ts'
import styles from './UnderwritingFlags.module.css'

interface UnderwritingFlagsProps {
    flags: VerificationFlags
}

export const UnderwritingFlags: React.FC<UnderwritingFlagsProps> = ({ flags }) => {
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'PASS': return 'green'
            case 'REVIEW': return 'yellow'
            case 'FAIL': return 'red'
            default: return 'gray'
        }
    }

    return (
        <div className={styles.flagsContainer}>
            {/* Status Banner */}
            <div className={`${styles.statusBanner} ${styles[getStatusColor(flags.overall_status)]}`}>
                <div className={styles.statusHeader}>
                    <Shield className={styles.statusIcon} />
                    <h3>UNDERWRITING FLAGS - {flags.overall_status}</h3>
                </div>
                <div className={styles.statusText}>
                    {flags.overall_status === 'FAIL' && 'Critical issues detected. Further review required.'}
                    {flags.overall_status === 'REVIEW' && 'Minor issues detected. Review recommended.'}
                    {flags.overall_status === 'PASS' && 'No critical issues detected.'}
                </div>
            </div>

            {/* Summary */}
            {flags.summary && (
                <div className={styles.summary}>
                    <strong>Summary:</strong> {flags.summary}
                </div>
            )}

            {/* Stats Row */}
            <div className={styles.statsRow}>
                <div className={`${styles.statCard} ${styles.redCard}`}>
                    <div className={styles.statValue}>{flags.red_flags}</div>
                    <div className={styles.statLabel}>🚨 Red Flags</div>
                </div>
                <div className={`${styles.statCard} ${styles.yellowCard}`}>
                    <div className={styles.statValue}>{flags.yellow_flags}</div>
                    <div className={styles.statLabel}>⚠️ Yellow Flags</div>
                </div>
                <div className={`${styles.statCard} ${styles.blueCard}`}>
                    <div className={styles.statValue}>{flags.risk_score.toFixed(2)}</div>
                    <div className={styles.statLabel}>📊 Risk Score</div>
                </div>
            </div>

            {/* Individual Flags */}
            <div className={styles.flagsList}>
                {flags.flags.length === 0 ? (
                    <div className={styles.noFlags}>
                        <CheckCircle className={styles.checkIcon} />
                        <span>No red or yellow flags detected. Income verification complete.</span>
                    </div>
                ) : (
                    flags.flags.map((flag, idx) => (
                        <div key={idx} className={`${styles.flagCard} ${styles[flag.severity.toLowerCase()]}`}>
                            <div className={styles.flagHeader}>
                                <span className={styles.severityBadge}>{flag.severity}</span>
                                <span className={styles.flagTitle}>{flag.title}</span>
                                {flag.category && <span className={styles.categoryTag}>{flag.category}</span>}
                            </div>

                            <p className={styles.flagDescription}>{flag.description}</p>

                            <div className={styles.flagSection}>
                                <strong>IMPACT:</strong>
                                <p>{flag.impact}</p>
                            </div>

                            <div className={styles.flagSection}>
                                <strong>RECOMMENDATION:</strong>
                                <p>{flag.recommendation}</p>
                            </div>

                            {flag.agency_reference && (
                                <div className={styles.reference}>
                                    <strong>Reference:</strong> {flag.agency_reference}
                                </div>
                            )}

                            {flag.data_points && flag.data_points.length > 0 && (
                                <div className={styles.supportingData}>
                                    <strong>SUPPORTING DATA</strong>
                                    <ul>
                                        {flag.data_points.map((point, i) => (
                                            <li key={i}>{point}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            {/* Next Steps */}
            {flags.total_flags > 0 && (
                <div className={styles.nextSteps}>
                    <h4>Next Steps</h4>
                    <ol>
                        <li>Review breakdown table for accuracy</li>
                        <li>Verify income sources with documentation</li>
                        <li>Address each flag according to recommendations</li>
                        <li>Consult with underwriter if needed</li>
                    </ol>
                </div>
            )}
        </div>
    )
}

export default UnderwritingFlags

